/* Personalised cart thumbnails. Works after Dawn replaces cart HTML via Ajax.
   Artwork settings live on each Shopify line, never in local/session storage.
   No cart mutations or additional cart requests are performed by this file. */
(() => {
  'use strict';
  const tag = 'personalised-cart-thumbnail';
  if (customElements.get(tag)) return;
  const fonts = new Map();
  let adobeStylesheet;

  function number(value, min, max) {
    if (value === undefined || value === null || String(value).trim() === '') return null;
    const result = Number(value);
    return Number.isFinite(result) && result >= min && result <= max ? result : null;
  }
  function shopifyAsset(value, kind) {
    if (typeof value !== 'string' || !value || value.length > 2048) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
      const shopifyCDN = url.hostname === 'cdn.shopify.com';
      const shopifyFonts = url.hostname === 'fonts.shopifycdn.com';
      const storeCDN = url.origin === window.location.origin && url.pathname.startsWith('/cdn/');
      if (kind === 'font' ? !(shopifyCDN || shopifyFonts || storeCDN) : !(shopifyCDN || storeCDN)) return null;
      return url.href;
    } catch (_) { return null; }
  }
  function readConfig(data) {
    if (data.version !== '1' || typeof data.name !== 'string' || (!data.name.trim() && !data.value2?.trim() && !data.value3?.trim())) return null;
    const image = shopifyAsset(data.image, 'image');
    const x = number(data.x, -100, 200), xf = number(data.xFine, -100, 100);
    const y = number(data.y, -100, 200), yf = number(data.yFine, -100, 100);
    const size = number(data.size, 0.01, 100);
    const spacing = number(data.spacing, -10, 10);
    const weight = number(data.weight, 1, 1000);
    if (!image || [x, xf, y, yf, size, spacing, weight].includes(null)) return null;
    if (typeof data.color !== 'string' || data.color.length > 100 || !CSS.supports('color', data.color)) return null;
    if (!['normal', 'italic', 'oblique'].includes(data.fontStyle)) return null;
    if (!['shopify', 'adobe_noto', 'adobe_good'].includes(data.font)) return null;
    const fontURL = data.font === 'shopify' ? shopifyAsset(data.fontUrl, 'font') : null;
    if (data.font === 'shopify' && !fontURL) return null;
    return {
      image, name: data.name, x: x + xf, y: y + yf, size, spacing, weight,
      color: data.color, style: data.fontStyle, source: data.font, fontURL,
      punctuation: data.punctuation === 'comma' ? ',' : data.punctuation === 'hyphen' ? '-' : ''
    };
  }
  function timeout(promise, ms = 12000) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Preview resource timed out')), ms);
      promise.then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
    });
  }
  function loadAdobeStylesheet() {
    if (adobeStylesheet) return adobeStylesheet;
    adobeStylesheet = new Promise((resolve, reject) => {
      let link = document.querySelector('link[data-pp-adobe-font]');
      if (!link) link = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(el => el.href === 'https://use.typekit.net/rad4gkb.css');
      if (link?.sheet) { resolve(); return; }
      const create = !link;
      if (create) {
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://use.typekit.net/rad4gkb.css';
        link.dataset.ppAdobeFont = '';
      }
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', reject, { once: true });
      if (create) document.head.appendChild(link);
    });
    return adobeStylesheet;
  }
  function loadFont(config) {
    const key = [config.source, config.fontURL, config.weight, config.style].join('|');
    if (fonts.has(key)) return fonts.get(key);
    const task = timeout((async () => {
      if (config.source === 'adobe_noto' || config.source === 'adobe_good') {
        const adobeFamily = config.source === 'adobe_good' ? 'ff-good-web-pro-condensed' : 'noto-sans-display';
        await loadAdobeStylesheet();
        const faces = await document.fonts.load(`${config.style} ${config.weight} 16px "${adobeFamily}"`);
        if (!faces.length) throw new Error('Adobe preview font unavailable');
        return `"${adobeFamily}", sans-serif`;
      }
      const family = `PersonalisedCartFont${fonts.size + 1}`;
      const face = new FontFace(family, `url(${JSON.stringify(config.fontURL)})`, {
        weight: String(config.weight), style: config.style
      });
      await face.load();
      document.fonts.add(face);
      return `"${family}", sans-serif`;
    })());
    fonts.set(key, task);
    return task;
  }

  class PersonalisedCartThumbnail extends HTMLElement {
    connectedCallback() {
      if (this.rendered || this.observer) return;
      // Children may not yet exist when a registered element is parsed.
      this.observer = new MutationObserver(() => this.start());
      this.observer.observe(this, { childList: true });
      this.start();
    }
    start() {
      if (this.loading || this.rendered) return;
      const fallback = this.querySelector('[data-pp-fallback]');
      if (!fallback) return;
      this.observer?.disconnect();
      this.observer = null;
      const config = readConfig(this.dataset);
      if (!config) return;
      if (!window.personalisedTextBoxesInstalled && !document.querySelector('script[data-pp-box-loader]')) {
        const url = shopifyAsset(this.dataset.boxScript, 'image');
        if (url) {
          const script = document.createElement('script');
          script.src = url;
          script.dataset.ppBoxLoader = '';
          document.head.appendChild(script);
        }
      }
      this.loading = true;
      const run = this.run = {};
      this.render(config, fallback, run).finally(() => {
        if (this.run === run) this.loading = false;
      });
    }
    async render(config, fallback, run) {
      try {
        const image = new Image();
        image.alt = '';
        image.decoding = 'async';
        image.style.cssText = 'display:block;width:100%;max-width:100%;height:auto;margin:0;border:0;';
        const loaded = timeout(new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = config.image;
        }));
        const [family] = await Promise.all([loadFont(config), loaded]);
        if (!this.isConnected || this.run !== run) return;

        const frame = document.createElement('div');
        frame.style.cssText = 'position:relative;width:100%;overflow:hidden;container-type:inline-size;';
        frame.setAttribute('role', 'img');
        frame.setAttribute('aria-label', `Personalised print: ${config.name}`);
        const text = document.createElement('div');
        text.setAttribute('aria-hidden', 'true');
        text.style.cssText = 'position:absolute;z-index:2;transform:translate(0,-50%);display:flex;align-items:baseline;justify-content:flex-start;max-width:90%;line-height:1.15;pointer-events:none;margin:0;padding:0;text-transform:none;text-align:left;';
        Object.assign(text.style, {
          left: `${config.x}%`, top: `${config.y}%`, fontFamily: family,
          fontSize: `${config.size}cqw`, fontWeight: String(config.weight),
          fontStyle: config.style, letterSpacing: `${config.spacing}em`, color: config.color
        });
        const name = document.createElement('span');
        name.style.cssText = 'display:inline;margin:0;padding:0;font:inherit;color:inherit;letter-spacing:inherit;line-height:inherit;';
        name.textContent = config.name;
        const punctuation = document.createElement('span');
        punctuation.style.cssText = 'display:inline;margin:0;padding:0;font:inherit;color:inherit;letter-spacing:inherit;line-height:inherit;margin-left:-2px;';
        punctuation.textContent = config.punctuation;
        text.append(name, punctuation);
        if (!config.name.trim()) text.style.display = 'none';
        frame.append(image, text);
        const setBox = (element, n) => {
          let config;
          try { config = JSON.parse(this.dataset[`box${n}`] || 'null'); } catch (_) { return; }
          if (!config || !['fit', 'fill'].includes(config.mode)) return;
          if (!(number(config.width, 0.1, 100) && number(config.height, 0.1, 100))) return;
          element.dataset.ppBoxMode = config.mode;
          element.dataset.ppBoxWidth = String(config.width);
          element.dataset.ppBoxHeight = String(config.height);
        };
        setBox(text, 1);
        const overlays = [{ element: text, size: config.size }];
        for (const n of [2, 3]) {
          const value = this.dataset[`value${n}`];
          if (!value || !value.trim()) continue;
          let layout;
          try { layout = JSON.parse(this.dataset[`layout${n}`] || 'null'); } catch (_) { continue; }
          if (!layout || typeof layout !== 'object') continue;
          const extra = readConfig({ ...this.dataset, name: value,
            x: layout.x, y: layout.y, xFine: '0', yFine: '0',
            size: layout.size, color: layout.color, punctuation: layout.punctuation });
          if (!extra) continue;
          const overlay = text.cloneNode(true);
          overlay.style.display = 'flex';
          delete overlay.dataset.ppBoxMode;
          delete overlay.dataset.ppBoxWidth;
          delete overlay.dataset.ppBoxHeight;
          setBox(overlay, n);
          Object.assign(overlay.style, { left: `${extra.x}%`, top: `${extra.y}%`, color: extra.color, fontSize: `${extra.size}cqw` });
          overlay.children[0].textContent = value;
          overlay.children[1].textContent = extra.punctuation;
          frame.appendChild(overlay);
          overlays.push({ element: overlay, size: extra.size });
        }
        this.appendChild(frame);
        const sizeOverlays = width => {
          if (width <= 0) return;
          overlays.forEach(({ element, size }) => {
            element.style.fontSize = `${width * size / 100}px`;
            element.style.webkitTextSizeAdjust = '100%';
          });
        };
        this.resizeObserver?.disconnect();
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(entries => sizeOverlays(entries[0].contentRect.width));
          this.resizeObserver.observe(frame);
        }
        sizeOverlays(parseFloat(getComputedStyle(frame).width));
        fallback.hidden = true;
        fallback.style.display = 'none';
        this.rendered = true;
      } catch (_) {
        // Missing/deleted artwork or a blocked font leaves the original image.
        // Never interfere with quantity updates, removal, or checkout.
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.observer = null;
      this.run = null;
      this.loading = false;
    }
  }
  customElements.define(tag, PersonalisedCartThumbnail);
})();
